import { FiEdit2, FiTrash2, FiFileText } from "react-icons/fi";
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Textarea, Button, Icon, useDisclosure, Text, Input, HStack, MenuItem, Menu, MenuButton, IconButton, MenuList, Tooltip, Portal } from "@chakra-ui/react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { BsRobot } from "react-icons/bs";
import { TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import { DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { useState, useEffect } from "react";
import { updateChatSessionHeaders } from "services/AgentChatService";
import showToast from "hooks/useCustomToast";
import { ChevronDownIcon } from "@chakra-ui/icons";

const ChatNameComponent: React.FC<{
	chatId: string;
	sessionHeader: DarwinHeader;
	setSessionHeader: (header: DarwinHeader) => void;
	onDelete: () => void;
	onAfterRename: () => void;
	onToggleArtifacts?: () => void;
	isArtifactsPanelVisible?: boolean;
	analystsList?: Header[];
	selectedAnalyst?: Header | null;
	onAnalystChange?: (analystId: string) => void;
}> = ({
	chatId,
	sessionHeader,
	setSessionHeader,
	onDelete,
	onAfterRename,
	onToggleArtifacts,
	isArtifactsPanelVisible,
	analystsList,
	selectedAnalyst,
	onAnalystChange,
}) => {
		const [editingId, setEditingId] = useState<string | null>(null);
		const [editValue, setEditValue] = useState("");

		// Description state — kept locally so no refetch is needed after save
		const [localDescription, setLocalDescription] = useState<string>(sessionHeader?.description || "");
		const [descDraft, setDescDraft] = useState("");
		const [isEditingDesc, setIsEditingDesc] = useState(false);
		const { isOpen: isDescModalOpen, onOpen: onDescModalOpen, onClose: onDescModalClose } = useDisclosure();

		// Sync description when the active chat changes
		useEffect(() => {
			setLocalDescription(sessionHeader?.description || "");
		}, [sessionHeader]);

		const handleRename = async (id: string) => {
			if (!editValue.trim()) {
				setEditingId(null);
				return;
			}
			const success = await updateChatSessionHeaders([id], editValue.trim(), undefined, false);
			if (success) {
				showToast("Chat renamed", "", "success");
				setSessionHeader({ ...sessionHeader, name: editValue.trim() });
				onAfterRename();
			}
			setEditingId(null);
		};

		const openDescModal = (editMode: boolean) => {
			setDescDraft(localDescription);
			setIsEditingDesc(editMode);
			onDescModalOpen();
		};

		const handleDescSave = async () => {
			const success = await updateChatSessionHeaders([chatId], sessionHeader?.name, descDraft.trim(), false);
			if (success) {
				setLocalDescription(descDraft.trim());
				showToast("Description saved", "", "success");
				onDescModalClose();
			}
		};

		const handleDescModalClose = () => {
			setIsEditingDesc(false);
			onDescModalClose();
		};

		return (
			<>
				<HStack
					spacing={2}
					px={3}
					py={isArtifactsPanelVisible ? 0 : 1}
					bg="white"
					borderBottom="1px solid"
					borderColor="gray.200"
					boxShadow="sm"
					justify="space-between"
					width="100%"
					flexShrink={0}
				>
					{/* Left: name + dropdown */}
					<HStack spacing={1} minW={0} flex={1}>
						{editingId === chatId ? (
							<HStack spacing={1}>
								<Input
									size="sm"
									autoFocus
									value={editValue}
									onChange={(e) => setEditValue(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") handleRename(chatId);
										if (e.key === "Escape") setEditingId(null);
									}}
									onClick={(e) => e.stopPropagation()}
									bg="white"
									color="fg.default"
									border="1px solid"
									borderColor="gray.300"
									borderRadius="md"
									fontSize="sm"
								/>
								<IconButton
									aria-label="Save"
									icon={<Icon as={FaCheck} boxSize={3.5} color="green.500" />}
									size="xs"
									variant="ghost"
									onMouseDown={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleRename(chatId);
									}}
								/>
								<IconButton
									aria-label="Cancel"
									icon={<Icon as={FaTimes} boxSize={3.5} color="red.500" />}
									size="xs"
									variant="ghost"
									onMouseDown={(e) => {
										e.preventDefault();
										setEditingId(null);
									}}
								/>
							</HStack>
						) : (
							<Menu placement="bottom-start">
								<MenuButton
									as={Button}
									variant="ghost"
									size="xs"
									minH="auto"
									py={0}
									px={1}
									fontSize={isArtifactsPanelVisible ? "xs" : "sm"}
									fontWeight={isArtifactsPanelVisible ? "700" : "600"}
									color="gray.800"
									_hover={{ bg: "gray.100" }}
									_active={{ bg: "gray.100" }}
									rightIcon={<Icon as={ChevronDownIcon} boxSize={3.5} color="gray.500" />}
									aria-label="Chat options"
									isDisabled={!chatId}
									onClick={(e) => e.stopPropagation()}
									textAlign="left"
									w="auto"
									maxW="100%"
								>
									<Text noOfLines={1} m={0} as="span" overflow="hidden" textOverflow="ellipsis">
										{sessionHeader?.name}
									</Text>
								</MenuButton>
								<MenuList minW="150px" boxShadow="md" zIndex={1001}>
									<MenuItem
										icon={<FiEdit2 size={14} />}
										fontSize="sm"
										onClick={(e) => {
											e.stopPropagation();
											setEditValue(sessionHeader?.name || "");
											setEditingId(chatId);
										}}
									>
										Rename
									</MenuItem>
									<MenuItem
										icon={<FiFileText size={14} />}
										fontSize="sm"
										onClick={(e) => {
											e.stopPropagation();
											openDescModal(!localDescription);
										}}
									>
										{localDescription ? "View Description" : "Add Description"}
									</MenuItem>
									<MenuItem
										icon={<FiTrash2 size={14} />}
										fontSize="sm"
										color="red.500"
										onClick={(e) => {
											e.stopPropagation();
											onDelete();
										}}
									>
										Delete
									</MenuItem>
								</MenuList>
							</Menu>
						)}
					</HStack>

					{/* Right: analyst dropdown + artifacts toggle */}
					<HStack spacing={2}>
						{analystsList && analystsList.length > 0 && (
							<Tooltip label={selectedAnalyst?.name || "Select analyst"} hasArrow placement="bottom">
								<Box as="span" display="inline-block">
									<Menu>
										<MenuButton
											as={IconButton}
											icon={<Icon as={BsRobot} boxSize={isArtifactsPanelVisible ? 4 : 5} color="purple.600" />}
											size={isArtifactsPanelVisible ? "2xs" : "xs"}
											variant="ghost"
											borderRadius="full"
											bg={isArtifactsPanelVisible ? "transparent" : "purple.50"}
											_hover={{ bg: "purple.50" }}
											_active={{ bg: "purple.50" }}
											aria-label="Select analyst"
										/>
										<Portal>
											<MenuList maxH="300px" overflowY="auto" zIndex={1500}>
												{analystsList.length === 0 ? (
													<MenuItem isDisabled fontSize="sm" color="gray.500">
														No analysts available
													</MenuItem>
												) : (
													analystsList.map((analyst) => (
														<MenuItem
															key={analyst.id}
															onClick={() => analyst.id && onAnalystChange?.(analyst.id)}
															fontSize="sm"
															_hover={{ bg: "purple.50" }}
															_focus={{ bg: "purple.50" }}
															fontWeight="500"
															bg={selectedAnalyst?.id === analyst.id ? "purple.50" : undefined}
														>
															<HStack spacing={2}>
																{selectedAnalyst?.id === analyst.id && (
																	<Icon as={BsRobot} boxSize={4} color="purple.600" />
																)}
																<Text mb={0}>{analyst.name}</Text>
															</HStack>
														</MenuItem>
													))
												)}
											</MenuList>
										</Portal>
									</Menu>
								</Box>
							</Tooltip>
						)}
						{onToggleArtifacts && !isArtifactsPanelVisible && (
							<HStack
								spacing={2}
								px={1.5}
								py={0.5}
								borderRadius="md"
								cursor="pointer"
								color="gray.700"
								_hover={{ color: "gray.700", bg: "gray.100" }}
								onClick={(e) => {
									e.stopPropagation();
									onToggleArtifacts();
								}}
							>
								{/* <Icon as={FiLayers} boxSize={4} /> */}
								<Text fontSize="xs" fontWeight="500" m={0}>Show Artifacts</Text>
								<Icon as={TbLayoutSidebarRightExpandFilled} boxSize={5} />
							</HStack>
						)}
					</HStack>
				</HStack>

				{/* Description Modal */}
				<Modal isOpen={isDescModalOpen} onClose={handleDescModalClose} size="md">
					<ModalOverlay />
					<ModalContent>
						<ModalHeader fontSize="md" fontWeight="semibold">
							{isEditingDesc ? (localDescription ? "Edit Description" : "Add Description") : "Description"}
						</ModalHeader>
						<ModalCloseButton />
						<ModalBody pb={4}>
							{isEditingDesc ? (
								<Textarea
									value={descDraft}
									onChange={(e) => setDescDraft(e.target.value)}
									placeholder="Enter a description for this chat..."
									rows={5}
									resize="vertical"
									_focus={{ borderColor: "purple.600" }}
									autoFocus
								/>
							) : (
								<Box bg="purple.50" p={2} borderRadius="md" border="1px solid" borderColor="purple.200">
									<Text fontSize="sm" color="fg.default" whiteSpace="pre-wrap">
										{localDescription}
									</Text>
								</Box>
							)}
						</ModalBody>
						<ModalFooter gap={2}>
							{isEditingDesc ? (
								<>
									<Button variant="ghost" size="sm" onClick={handleDescModalClose}>
										Cancel
									</Button>
									<Button colorScheme="purple" size="sm" onClick={handleDescSave}>
										Save
									</Button>
								</>
							) : (
								<Button
									leftIcon={<Icon as={FiEdit2} boxSize={3} />}
									size="sm"
									colorScheme="purple"
									variant="outline"
									onClick={() => setIsEditingDesc(true)}
								>
									Edit
								</Button>
							)}
						</ModalFooter>
					</ModalContent>
				</Modal>
			</>
		);
	};

export default ChatNameComponent;